/**
 * Acumulación de frames del **modo autónomo** (004 · US8 · T061; FR-042, FR-043;
 * `docs/vpp-protocol.md` §14.3/§14.4).
 *
 * Cuando el marcador pierde el canal deja de pintar Bootstrap Codes y pasa a emitir **frames que
 * deriva él solo** de su clave de terminal, un QR por slot de 200 ms. Para el trabajador no cambia
 * nada: apunta la cámara igual. Quien distingue es Staff, **por el prefijo del texto**:
 *
 * ```text
 * FM1B:<ULID26>:<slot>:<TOKEN20>   Bootstrap Code   → flujo normal (§4): POST /challenges
 * FM1A:<ULID26>:<slot>:<TOKEN16>   frame autónomo   → este flujo:        POST /marks/autonomous
 * FM1C:<c8>:<i>:<t16>              frame de challenge → `features/attendance/frames.ts`
 * ```
 *
 * **Función pura**, sin timers ni DOM, igual que `frames.ts` del modo normal. Las diferencias con
 * aquel, que son justo lo que hace falta implementar aparte:
 *
 * - La unidad **no es un índice `1..n` dentro de una secuencia conocida**, sino un **slot absoluto**
 *   (`floor(hora_sincronizada / slot_ms)`), que Staff no puede predecir ni acotar: el marcador sigue
 *   emitiendo indefinidamente mientras no recupere la red.
 * - Por eso no hay "fin de secuencia" ni "mitad final": la evidencia sale cuando hay slots
 *   suficientes o al vencer el plazo.
 * - El servidor exige slots **estrictamente crecientes con huecos de como mucho un slot**
 *   (FR-043). Un frame que rompa esa forma se **descarta** en vez de enviarse: incluirlo haría
 *   fallar toda la evidencia en lugar de solo ese frame, exactamente como en el modo normal.
 * - El `tsid` es el de la sesión del marcador y lo trae **cada** frame: solo entran los del
 *   marcador que se está mirando.
 *
 * `dt_ms` se cuenta desde el **primer** frame observado con `performance.now()` (research R-6): el
 * reloj de pared del teléfono es telemetría y nunca entra en un cálculo (constitución IV).
 */
import {
  AutonomousTokenSchema,
  TerminalSessionId,
  decodeFerminQr,
  type AutonomousFrame,
} from '@fermin/contracts';

/**
 * Plazo máximo de captura antes de enviar lo que haya (FR-021 aplicado al modo autónomo). Es el
 * mismo valor que `EVIDENCE_DEADLINE_MS` del modo normal y por la misma razón: más de 2.5 s
 * apuntando a la pantalla se percibe como que la aplicación se colgó.
 */
export const AUTONOMOUS_EVIDENCE_DEADLINE_MS = 2_500;

/**
 * Slots distintos que el servidor exige por defecto (`AUTONOMOUS_MIN_FRAMES`). Staff no conoce la
 * configuración del servidor, así que usa el valor por defecto del protocolo (§14.7) y envía en
 * cuanto los tiene; si el servidor exige más, responde `422` y Staff reintenta.
 */
export const AUTONOMOUS_MIN_FRAMES = 4;

/**
 * Salto máximo entre dos slots aceptados: `1` contiguos, `2` con un slot perdido. Es la misma cota
 * que aplica el servidor (`modules/vpp/autonomous.ts#AUTONOMOUS_MAX_SLOT_STEP`); mantenerla aquí
 * evita mandar evidencia que ya se sabe que va a fallar.
 */
export const AUTONOMOUS_MAX_SLOT_STEP = 2;

/** Tope del contrato para `dt_ms`. */
const MAX_DT_MS = 30_000;

export interface CollectedAutonomousFrame {
  slot: number;
  token: string;
  /** Milisegundos desde el primer frame observado. */
  dtMs: number;
}

export interface AutonomousCollection {
  /** En orden de captura, únicos por slot y estrictamente crecientes. */
  frames: CollectedAutonomousFrame[];
  /** `tss_…` del marcador que se está mirando; `null` mientras no hay ningún frame. */
  tsid: string | null;
  /** Instante monotónico del primer frame; `null` mientras no hay ninguno. */
  firstAtMs: number | null;
  /** Mayor slot ya aceptado; `null` mientras no hay ninguno. */
  maxSlot: number | null;
}

export function emptyAutonomousCollection(): AutonomousCollection {
  return { frames: [], tsid: null, firstAtMs: null, maxSlot: null };
}

/**
 * `true` si el texto leído es un frame autónomo (`FM1A`). Es lo que decide si la marcación va por
 * `POST /challenges` o por `POST /marks/autonomous`, sin que el trabajador note la diferencia.
 */
export function isAutonomousQr(text: string): boolean {
  return decodeFerminQr(text)?.kind === 'autonomous';
}

/** `tss_…` del marcador que emitió ese frame, o `null` si el texto no es un `FM1A` válido. */
export function autonomousTsidOf(text: string): string | null {
  const decoded = decodeFerminQr(text);
  if (decoded === null || decoded.kind !== 'autonomous') {
    return null;
  }
  return TerminalSessionId.safeParse(decoded.tsid).success ? decoded.tsid : null;
}

/**
 * Incorpora una lectura. Devuelve **la misma colección** (identidad estable) cuando no aporta nada:
 * texto ajeno, otro marcador, token mal formado, slot repetido, fuera de orden o con un hueco
 * demasiado grande.
 */
export function collectAutonomousFrame(
  collection: AutonomousCollection,
  read: { text: string; capturedAtMs: number },
): AutonomousCollection {
  const decoded = decodeFerminQr(read.text);
  if (decoded === null || decoded.kind !== 'autonomous') {
    return collection;
  }
  if (!TerminalSessionId.safeParse(decoded.tsid).success) {
    return collection;
  }
  if (!AutonomousTokenSchema.safeParse(decoded.token).success) {
    return collection;
  }
  if (!Number.isSafeInteger(decoded.slot) || decoded.slot < 0) {
    return collection;
  }
  // Un marcador distinto a mitad de captura: se ignora (apuntar a dos pantallas no es evidencia).
  if (collection.tsid !== null && decoded.tsid !== collection.tsid) {
    return collection;
  }
  if (collection.maxSlot !== null) {
    const step = decoded.slot - collection.maxSlot;
    if (step < 1 || step > AUTONOMOUS_MAX_SLOT_STEP) {
      return collection;
    }
  }

  const firstAtMs = collection.firstAtMs ?? read.capturedAtMs;
  const dtMs = Math.min(MAX_DT_MS, Math.max(0, Math.round(read.capturedAtMs - firstAtMs)));
  return {
    frames: [...collection.frames, { slot: decoded.slot, token: decoded.token, dtMs }],
    tsid: decoded.tsid,
    firstAtMs,
    maxSlot: decoded.slot,
  };
}

/** Motivo por el que la evidencia autónoma ya puede enviarse (solo diagnóstico interno). */
export type AutonomousEvidenceTrigger = 'ENOUGH_FRAMES' | 'DEADLINE';

/**
 * La evidencia sale cuando hay `minFrames` slots distintos —lo mínimo que el servidor aceptará— o
 * a los 2.5 s del primero, lo que ocurra antes. No hay "fin de secuencia": el marcador autónomo no
 * tiene final.
 */
export function autonomousEvidenceTrigger(
  collection: AutonomousCollection,
  nowMs: number,
  options: { minFrames?: number; deadlineMs?: number } = {},
): AutonomousEvidenceTrigger | null {
  const minFrames = options.minFrames ?? AUTONOMOUS_MIN_FRAMES;
  const deadlineMs = options.deadlineMs ?? AUTONOMOUS_EVIDENCE_DEADLINE_MS;
  if (collection.frames.length === 0 || collection.firstAtMs === null) {
    return null;
  }
  if (collection.frames.length >= minFrames) {
    return 'ENOUGH_FRAMES';
  }
  if (nowMs - collection.firstAtMs >= deadlineMs) {
    return 'DEADLINE';
  }
  return null;
}

/** Cuerpo de `POST /marks/autonomous`: los frames tal cual se observaron (contrato `AutonomousFrame`). */
export function toAutonomousFrames(collection: AutonomousCollection): AutonomousFrame[] {
  return collection.frames.map((frame) => ({
    slot: frame.slot,
    token: frame.token,
    dt_ms: frame.dtMs,
  }));
}

/** Avance 0–100 para el anillo de progreso: slots observados sobre los requeridos. */
export function autonomousProgress(
  collection: AutonomousCollection,
  minFrames: number = AUTONOMOUS_MIN_FRAMES,
): number {
  if (minFrames <= 0) {
    return 0;
  }
  const ratio = collection.frames.length / minFrames;
  return Math.max(0, Math.min(100, Math.round(ratio * 100)));
}
